import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { connect, ConnectedProps } from "react-redux";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import PageHeader from "&styled/page-header";
import SelectComponent from "&styled/form/select";
import { RootState } from "&store/store";
import { getCurrentUser } from "&config/getCurrentUser";
import { colorPalette } from "&config/colors";
import { WALEE_BASE_URL } from "&config/url";

type ReduxProps = ConnectedProps<typeof connector>;

type ConsumerNumberRequestJobRow = {
  _id: string;
  serviceId: string;
  serviceName: string;
  fileName: string;
  processedRecords: number;
  failedRecords: number;
  successRecords: number;
  createdAt: string;
};

type ConsumerNumberRequestsResponse = {
  data: Array<Record<string, any>>;
  current: number;
  perPage: number;
  pages: number;
  total: number;
};

const ConsumerNumberRequestsComponent = (props: ReduxProps) => {
  const history = useHistory();
  const { token } = props;
  const currentUser = getCurrentUser(token) || { role: { permissions: {} } };
  const permissions = currentUser.role?.permissions || {};
  const canView = !!permissions.ConsumerNumberRequests?.viewAll;
  const canUpload = !!permissions.ConsumerNumberRequests?.uploadFile;
  const [rows, setRows] = useState<ConsumerNumberRequestJobRow[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [startDateInput, setStartDateInput] = useState("");
  const [endDateInput, setEndDateInput] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [serviceId, setServiceId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [services, setServices] = useState<Array<{ label: string; value: string }>>([]);

  useEffect(() => {
    const fetchConsumerNumberRequests = async () => {
      if (!token) return;

      setLoading(true);
      try {
        const response = await axios.get<ConsumerNumberRequestsResponse>(
          `${WALEE_BASE_URL}/consumer-number-requests/jobs`,
          {
            headers: { "x-auth-token": token },
            params: {
              page: page + 1,
              perPage: pageSize,
              search: search || undefined,
              dateFrom: dateFrom || undefined,
              dateTo: dateTo || undefined,
            },
          }
        );
        const result = response.data;
        const requestRows = Array.isArray(result.data) ? result.data : [];

        setRows(
          requestRows.map((item, index): ConsumerNumberRequestJobRow => ({
            _id: item._id || `${item.serviceId}-${item.createdAt}-${index}`,
            serviceId: item.serviceId || "",
            serviceName: item.serviceName || "",
            fileName: item.fileName || "",
            processedRecords: Number(item.processedRecords || 0),
            failedRecords: Number(item.failedRecords || 0),
            successRecords: Number(item.successRecords || 0),
            createdAt: item.createdAt || "",
          }))
        );
        setRowCount(result.total || 0);
      } catch (error) {
        console.error("Failed to fetch consumer requests", error);
      } finally {
        setLoading(false);
      }
    };

    fetchConsumerNumberRequests();
  }, [token, page, pageSize, refreshKey, search, dateFrom, dateTo]);

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(0);
    setSearch(searchInput.trim());
    setDateFrom(startDateInput);
    setDateTo(endDateInput);
  };

  useEffect(() => {
    const fetchServices = async () => {
      if (!token) return;

      try {
        const response = await axios.get(`${WALEE_BASE_URL}/services/get`, {
          headers: { "x-auth-token": token },
        });

        const mappedServices = (response.data || []).map((item) => ({
          label: item.serviceName || item.name || item.serviceId,
          value: item.serviceId || item._id,
        }));

        setServices(mappedServices);
      } catch (error) {
        console.error("Failed to fetch services", error);
      }
    };

    fetchServices();
  }, [token]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "fileName", headerName: "File Name", width: 280, headerAlign: "left", align: "left" },
      { field: "serviceId", headerName: "Service ID", width: 150, headerAlign: "left", align: "left" },
      { field: "serviceName", headerName: "Service Name", width: 180, headerAlign: "left", align: "left" },
      { field: "processedRecords", headerName: "Processed Records", width: 170, headerAlign: "left", align: "left" },
      { field: "successRecords", headerName: "Success Records", width: 160, headerAlign: "left", align: "left" },
      { field: "failedRecords", headerName: "Failed Records", width: 150, headerAlign: "left", align: "left" },
      {
        field: "createdAt",
        headerName: "Created At",
        width: 180,
        headerAlign: "left",
        align: "left",
        valueFormatter: ({ value }) => (value ? new Date(value).toLocaleString() : "-"),
      },
      {
        field: "view",
        headerName: "Actions",
        width: 80,
        sortable: false,
        filterable: false,
        headerAlign: "left",
        align: "left",
        renderCell: ({ row }) => (
          <IconButton
            aria-label="View consumer number records"
            title="View records"
            onClick={() => history.push(`/consumer-number-requests/${row._id}`)}
          >
            <RemoveRedEyeRoundedIcon />
          </IconButton>
        ),
      },
    ],
    [history]
  );

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    event.target.value = "";
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile || !serviceId || !token || isSubmitting) return;

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("serviceId", serviceId);

    setIsSubmitting(true);
    try {
      const headers = {
        "x-auth-token": token,
        "Authorization": process.env.REACT_APP_ADMIN_PRODUCTS
      };

      await axios.post(`${WALEE_BASE_URL}/consumer-number-requests/`, formData, { headers });
      setUploadOpen(false);
      setSelectedFile(null);
      setServiceId("");
      setRefreshKey((current) => current + 1);
      toast.success("Consumer number requests file uploaded successfully.", { position: "top-center" });
    } catch (error) {
      console.error("Failed to upload consumer requests file", error);
      const message = (error as any)?.response?.data?.message;
      toast.error(message || "Failed to upload consumer number requests file.", { position: "top-center" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!canView) {
    return (
      <>
        <PageHeader title="Consumer Number Requests" />
        <Box
          sx={{
            my: 2,
            borderTop: `4px solid ${colorPalette.primary}`,
            borderTopLeftRadius: "4px",
            borderTopRightRadius: "4px",
          }}
        >
          <Card>
            <CardContent>
              <Box sx={{ color: "#3C3C3C", fontSize: "14px" }}>You do not have permission to view this page.</Box>
            </CardContent>
          </Card>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <PageHeader title="Consumer Number Request Jobs" />
        {canUpload && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={() => setUploadOpen(true)}
              sx={{
                backgroundColor: colorPalette.primary,
                color: "#fff",
                textTransform: "none",
                borderRadius: "4px",
                boxShadow: "none",
                "&:hover": { backgroundColor: colorPalette.primary },
              }}
            >
              Upload File
            </Button>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          my: 2,
          borderTop: `4px solid ${colorPalette.primary}`,
          borderTopLeftRadius: "4px",
          borderTopRightRadius: "4px",
        }}
      >
        <Card>
          <CardContent>
            <Box
              component="form"
              onSubmit={handleSearch}
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}
            >
              <TextField
                size="small"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search files or services"
                inputProps={{ "aria-label": "Search files or services" }}
                sx={{ flex: "1 1 220px" }}
              />
              <TextField
                size="small"
                type="date"
                label="Start date"
                value={startDateInput}
                onChange={(event) => setStartDateInput(event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 170 }}
              />
              <TextField
                size="small"
                type="date"
                label="End date"
                value={endDateInput}
                onChange={(event) => setEndDateInput(event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 170 }}
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={<SearchIcon />}
                sx={{
                  minWidth: 112,
                  borderRadius: "4px",
                  backgroundColor: colorPalette.primary,
                  boxShadow: "none",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  "&:hover": { backgroundColor: colorPalette.primary, boxShadow: "none" },
                }}
              >
                Search
              </Button>
            </Box>

            <Box sx={{ height: 550, width: "100%", overflow: "auto" }}>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row._id}
                disableSelectionOnClick
                autoHeight
                paginationMode="server"
                page={page}
                pageSize={pageSize}
                rowsPerPageOptions={[25, 50, 100]}
                rowCount={rowCount}
                loading={loading}
                onPageChange={(newPage) => setPage(newPage)}
                onPageSizeChange={(newPageSize) => {
                  setPageSize(newPageSize);
                  setPage(0);
                }}
                sx={{
                  border: "none",
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#F7F7F7",
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select file</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1 }}>
            <Typography sx={{ fontSize: "14px", mb: 2, color: "#3C3C3C" }}>Choose a service and file to upload.</Typography>
            <Box sx={{ mb: 2 }}>
              <SelectComponent
                value={serviceId}
                onSelect={(event) => setServiceId(event.target.value)}
                menuItems={services}
                placeHolder="Select Service"
                size="small"
              />
            </Box>
            <Button
              component="label"
              variant="outlined"
              sx={{
                color: colorPalette.primary,
                borderColor: colorPalette.primary,
                textTransform: "none",
              }}
            >
              Select File
              <input hidden type="file" accept=".csv,.xlsx,.xls,.txt" onChange={handleFileChange} />
            </Button>
            {selectedFile && (
              <Typography sx={{ mt: 2, fontSize: "14px", color: "#3C3C3C" }}>
                Selected file: {selectedFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUploadOpen(false)} sx={{ textTransform: "none", color: "#3C3C3C" }}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadSubmit}
            variant="contained"
            disabled={!selectedFile || !serviceId || isSubmitting}
            sx={{
              backgroundColor: colorPalette.primary,
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: colorPalette.primary },
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} color="inherit" />
                Submitting...
              </Box>
            ) : (
              "Submit"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  token: state.login.token,
});

const connector = connect(mapStateToProps);
const ConsumerNumberRequestsComponentRedux = connector(ConsumerNumberRequestsComponent);

export { ConsumerNumberRequestsComponentRedux as ConsumerNumberRequestsComponent };
